import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-form';
import moment from 'moment';
import { parsePhoneNumber } from 'react-phone-number-input';
import PropTypes from 'prop-types';

import { Select, SchoolSearch, DateTimePicker } from '../../../Form';
import { getAllClubLocations } from '../../../../api/clubLocationsApi';
import { getApiAxiosInstance } from '../../../../utils/network';
import { required } from '../../../../utils/form-validation';
import { saveEvent } from '../../../../actions/event-actions';

import styles from './PostDualMatchPopup.scss';
import { clubGenderType } from './helpers';
import { gtag } from '../../../Analytics/gtag';
import { ClubTierTypes } from '../../../../utils/clubs';

function PostDualMatchPopup({
    isOpen,
    onToggle,
    clubDetails,
    history,
}) {
    const dispatch = useDispatch();
    const auth = useSelector((state) => state.auth);
    const coreUI = useSelector((state) => state.coreUI);

    const [defaultValues, setDefaultValues] = useState({});
    const [isLoading, setLoading] = useState(false);
    const [
        dualMatchSelectedOpponentSchool,
        setDualMatchSelectedOpponentSchool,
    ] = useState(null);
    const [clubLocations, setClubLocations] = useState([]);
    const [dualMatchLocationOptions, setDualMatchLocationOptions] = useState([]);
    const [
        dualMatchEventScheduleStartDate,
        setDualMatchEventScheduleStartDate,
    ] = useState(null);

    useEffect(() => {
        const timeZone = coreUI.types.timeZones.find((tz) => {
            return tz.id === 77;
        });
        const $tourDateStart = moment()
            .tz(timeZone.value, true)
            .add(2, 'days')
            .set({ hour: 8, minute: 0, second: 0 });
        const $tourDateEnd = moment()
            .tz(timeZone.value, true)
            .add(2, 'days')
            .set({ hour: 21, minute: 0, second: 0 });
        const $regDateStart = moment()
            .tz(timeZone.value, true)
            .set({ hour: 8, minute: 0, second: 0 });
        const $regDateEnd = moment()
            .tz(timeZone.value, true)
            .set({ hour: 21, minute: 0, second: 0 });

        const defaultLocation = {
            location: clubDetails?.location?.googleFormattedName || '',
            eventLocationType: '1',
        };
        const momentTz = moment.tz.guess();
        const matchedTz = momentTz
            ? coreUI.types.timeZones.find((tz) => {
                return tz.value?.toLowerCase() === momentTz?.toLowerCase();
            })
            : null;
        const defaultTz = matchedTz ? matchedTz.id : '77';
        setDefaultValues({
            club: parseInt(clubDetails.id),
            name: 'Untitled Event',
            eventPermissionType: 1,
            utrType: 1,
            isPTTEvent: false,
            eventSchedule: {
                eventStartUtc: $tourDateStart ? $tourDateStart.toISOString() : null,
                eventEndUtc: $tourDateEnd ? $tourDateEnd.toISOString() : null,
                registrationStartUtc: $regDateStart ? $regDateStart.toISOString() : null,
                registrationEndUtc: $regDateEnd ? $regDateEnd.toISOString() : null,
                drawPublishedUtc: null,
                timeZone: defaultTz, // America/New York
            },
            eventDivisions: [],
            eventQuestions: [],
            passwordEnabled: false,
            eventPassword: '',
            eventDirectors: [
                {
                    firstName: auth.user.firstName,
                    imgUrl: '/img/default-profile-icon-120-x-120.svg',
                    lastName: auth.user.lastName,
                    memberId: auth.user.id,
                    title: 'Head Provider',
                    eventDirectorRole: 2,
                },
            ],
            healthAndSafetyProtocols: [],
            eventLocations: [defaultLocation],
            cloneLastEvent: false,
            $clubJoin:
            clubDetails?.tierType?.id !== ClubTierTypes.TOURNAMENT &&
            clubDetails?.tierType?.id !== ClubTierTypes.PREMIUM_LITE,
            $verified: '2',
            eventType: 8,
            email: auth?.user?.email,
            phone: auth?.user?.phone,
        });
    }, []);

    const onSubmit = async (values) => {
        if (values.name) {
            values.name = values.name.trim();
        }
        values.utrType = values.$verified;
        values.eventClubJoinRequestType = values.$clubJoin ? 2 : 1;

        if (values.phone) {
            const parsedPhone = parsePhoneNumber(values.phone);
            values.phoneCountryCode = parsedPhone ? parsedPhone.country : null;
        }

        setLoading(true);
        const savedEvent = await dispatch(saveEvent(values, true, false));
        if (savedEvent?.id) {
            gtag('event', {
                category: 'Create Event',
                context: `Created College Dual Match for College - collegeClubId: ${clubDetails.id}`,
            });
            // segment track calls for college dual match - mapped to GA4
            if (window && window.analytics) {
                window.analytics.track('Created College Dual Match for College', {
                    userId: auth.user.id,
                    collegeClubId: clubDetails.id,
                });
            }
            history.push(`/events/${savedEvent.id}`);
        }
        setLoading(false);
    };

    const createLocationOption = (
        location,
        locationClubId,
        locationClubSchoolProfile,
    ) => ({
        id: location.id,
        clubId: locationClubId,
        value: location.location.googleFormattedName,
        label: `${location.name} - ${locationClubSchoolProfile.displayName}`,
    });

    const setLocationOptions = async (opponentSchoolClub) => {
        const { data } = await getAllClubLocations(
            getApiAxiosInstance(),
            opponentSchoolClub.id,
        );

        const locationOptions = [
            ...clubLocations.map((v) => createLocationOption(v, defaultValues.club, clubDetails.schoolProfile)),
            ...data.clubLocations.map((v) => createLocationOption(v, opponentSchoolClub.id, opponentSchoolClub.school)),
        ];

        setDualMatchLocationOptions(locationOptions);
    };

    const handleSchoolClubSelect = (
        opponentSchoolClub,
        currentClubIsHome,
        opponentClubIsHome,
        values,
        formApi,
    ) => {
        const currentSchoolClubName = clubDetails.schoolProfile.displayName;
        const opponentSchoolClubName = opponentSchoolClub.school.displayName;
        const dualMatchClubs = [
            {
                clubId: values.club,
                isHome: currentClubIsHome,
            },
            {
                clubId: opponentSchoolClub.id,
                isHome: opponentClubIsHome,
            },
        ];

        let name;
        if (currentClubIsHome) {
            name = `${opponentSchoolClubName} at ${currentSchoolClubName}`;
        } else if (opponentClubIsHome) {
            name = `${currentSchoolClubName} at ${opponentSchoolClubName}`;
        } else {
            name = `${opponentSchoolClubName} vs. ${currentSchoolClubName}`;
        }

        setDualMatchSelectedOpponentSchool(opponentSchoolClub);
        setLocationOptions(opponentSchoolClub);

        formApi.setValue('dualMatchClubs', dualMatchClubs);
        formApi.setValue('name', name);
    };

    const handleDualMatchLocationChange = (optionValue, values, formApi) => {
        const selectedMatchLocationOption = dualMatchLocationOptions.find(
            (option) => {
                return option.id === optionValue;
            },
        );
        const clubIdOfLocation = selectedMatchLocationOption.clubId;
        const currentClubIsHome = clubIdOfLocation === values.club;
        const opponentClubIsHome =
      clubIdOfLocation === dualMatchSelectedOpponentSchool.id;
        formApi.setValue(
            ['eventLocations', 0, 'location'],
            selectedMatchLocationOption.value,
        );
        handleSchoolClubSelect(
            dualMatchSelectedOpponentSchool,
            currentClubIsHome,
            opponentClubIsHome,
            values,
            formApi,
        );
    };

    const handleClearSchoolClubClick = (formApi, values) => {
        formApi.setValue('name', null);
        formApi.setValue('dualMatchClubs', null);
        formApi.setValue('clubLocationId', null);
        formApi.setValue('eventLocations', values.eventLocations);

        setDualMatchSelectedOpponentSchool(null);
    };

    useEffect(() => {
        if (isOpen) {
            const timezone = defaultValues?.eventSchedule?.timeZone || 77;
            const timezoneObj = coreUI.types.timeZones.find((tz) => {
                return tz.id === parseInt(timezone);
            });
            setDualMatchEventScheduleStartDate(
                defaultValues.eventSchedule
                    ? moment(defaultValues.eventSchedule.eventStartUtc).tz(
                        timezoneObj.value,
                    )
                    : null,
            );
            (async () => {
                const { data } = await getAllClubLocations(
                    getApiAxiosInstance(),
                    defaultValues.club,
                );
                setClubLocations(data.clubLocations);
            })();
            document.body.classList.add('no-scroll');
        } else {
            setDualMatchLocationOptions([]);
            document.body.classList.remove('no-scroll');
        }
        return () => document.body.classList.remove('no-scroll');
    }, [isOpen]);

    const findOpponentCollegeGender = clubDetails?.clubSubType?.value === clubGenderType.MENS ? 'M' : 'F';
    return (
        isOpen && (
            <Form
                onSubmit={(values) => onSubmit({ ...values })}
                defaultValues={defaultValues}
            >
                {(formApi) => {
                    const { submitForm, values } = formApi;
                    return (
                        <form onSubmit={submitForm}>
                            <div className={`m-0 ${styles.popup} ${styles.fullScr}`}>
                                <div className={`${styles.popupDailog}`}>
                                    <div className={`${styles.popupContent}`}>
                                        <div className={`${styles.popupHead}`}>
                                            <div
                                                className={`d-flex justify-content-between align-items-center ${
                                                    styles.innerRow
                                                }`}
                                            >
                                                <div className={`${styles.title}`}>Post dual match</div>
                                                <div
                                                    className={`${styles.close}`}
                                                    onClick={() => onToggle(false)}
                                                >
                                                    <span />
                                                    <span />
                                                </div>
                                            </div>
                                        </div>
                                        <div className={`${styles.popupBody}`}>
                                            <div
                                                className={`d-flex justify-content-between align-items-center ${
                                                    styles.innerRow
                                                }`}
                                            >
                                                <div className={`${styles.matchCard}`}>
                                                    <div className="form-group">
                                                        <label htmlFor="">Who are you playing?</label>
                                                        <div className={`${styles.formFieldWrap}`}>
                                                            <SchoolSearch
                                                                placeholder={`Enter a college team`}
                                                                handleSchoolSelect={(college) => handleSchoolClubSelect(
                                                                    college,
                                                                    true,
                                                                    false,
                                                                    values,
                                                                    formApi,
                                                                )}
                                                                clearAfterSelect={true}
                                                                closeAfterSelect={true}
                                                                displaySchoolNameAfterSelect={true}
                                                                showSearchClearIcon={true}
                                                                searchEndPoint={`/colleges?gender=${findOpponentCollegeGender}`}
                                                                entityId={values.club}
                                                                schoolClubSearch={true}
                                                                handleClearSchoolClick={() => handleClearSchoolClubClick(formApi, values)}
                                                                isProximitySearch={true}
                                                                clubLocation={clubDetails.location}
                                                                themeV3
                                                                label={'Opponent'}
                                                            />
                                                        </div>
                                                    </div>

                                                    {dualMatchLocationOptions?.length > 0 && (
                                                        <div className="form-group">
                                                            <label htmlFor="">
                                                                Match location, date, and time
                                                            </label>
                                                            <div className={`${styles.formFieldWrap}`}>
                                                                <Select
                                                                    key={`match-location`}
                                                                    id={`match-location`}
                                                                    label="Location"
                                                                    field={`clubLocationId`}
                                                                    value={
                                                                        dualMatchLocationOptions?.length > 0
                                                                            ? dualMatchLocationOptions[0]
                                                                            : ''
                                                                    }
                                                                    onChange={(optionValue) => handleDualMatchLocationChange(
                                                                        optionValue,
                                                                        values,
                                                                        formApi,
                                                                    )}
                                                                    hiddenDefault={true}
                                                                    options={dualMatchLocationOptions}
                                                                    validate={required}
                                                                    themeV3
                                                                />
                                                            </div>
                                                            <div className={`${styles.inlineRow}`}>
                                                                <div className={`${styles.formFieldWrap}`}>
                                                                    <DateTimePicker
                                                                        id="matchStart"
                                                                        field="eventSchedule.eventStartUtc"
                                                                        placeholderText="Select date"
                                                                        selected={dualMatchEventScheduleStartDate}
                                                                        minDate={moment().add('years', -1)}
                                                                        maxDate={null}
                                                                        handleDateChange={(date) => {
                                                                            setDualMatchEventScheduleStartDate(date);
                                                                        }}
                                                                        validate={required}
                                                                        timeLabel="Start time"
                                                                        themeV3
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className={`${styles.popupFoot}`}>
                                            <div
                                                className={`d-flex justify-content-between align-items-center ${
                                                    styles.innerRow
                                                }`}
                                            >
                                                <div className={`${styles.btnWrap}`}>
                                                    <button
                                                        type="button"
                                                        className={`btn text-uppercase ${
                                                            styles.btnWhiteOutline
                                                        }`}
                                                        onClick={() => onToggle(false)}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        disabled={isLoading}
                                                        type="submit"
                                                        className={`btn text-uppercase ${
                                                            styles.btnWhiteOutline
                                                        }`}
                                                        onClick={submitForm}
                                                    >
                                                        {isLoading ? (
                                                            <span>
                                                                <i className="fas fa-circle-notch fa-spin" />{' '}
                                                                Loading...
                                                            </span>
                                                        ) : (
                                                            'Create'
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    );
                }}
            </Form>
        )
    );
}

PostDualMatchPopup.propTypes = {
    clubDetails: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default PostDualMatchPopup;
